<template>
  <BaseDialog
    v-model="isGenerateDetailsModalOpen"
    :title="title"
    max-width="850px"
    persistent
    @close="handleCloseDialog"
    @click:outside="handleCloseDialog"
  >
    <div class="prevent-details-modal">
      <v-treeview
        dense
        :open-all="isTreeExpanded"
        hoverable
        :items="treeViewItems"
        :key="treeViewRefreshKey"
      />
    </div>
  </BaseDialog>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api';
import BaseDialog from '@/views/shared/components/BaseDialog.vue';

export default {
  components: { BaseDialog },
  props: {
    isOpen: { type: Boolean, required: true },
    title: { type: String, required: true },
    data: { type: Object, required: true }
  },
  setup (props, { emit }) {
    const isGenerateDetailsModalOpen = ref(false);
    const treeViewItems = ref([]);
    const treeViewRefreshKey = ref(0);

    const isTreeExpanded = computed(() => props.isOpen);

    const isEmptyValue = (value) => {
      if (value === null || value === undefined) return true;
      if (typeof value === 'string' && value.trim() === '') return true;
      if (Array.isArray(value)) return value.length === 0;
      return false;
    };

    const mapObjectToTreeViewItems = (inputObject, propertyPath = []) => {
      if (inputObject === null || inputObject === undefined) return [];

      const mappedTreeItems = [];

      if (Array.isArray(inputObject)) {
        inputObject.forEach((arrayValue, arrayIndex) => {
          if (isEmptyValue(arrayValue)) return;

          const childPropertyPath = [...propertyPath, `[${arrayIndex}]`];

          if (typeof arrayValue === 'object' && arrayValue !== null) {
            const childTreeItems = mapObjectToTreeViewItems(arrayValue, childPropertyPath);
            if (childTreeItems.length) {
              mappedTreeItems.push({
                id: childPropertyPath.join('.'),
                name: `[${arrayIndex}]`,
                children: childTreeItems
              });
            }
          } else {
            mappedTreeItems.push({
              id: childPropertyPath.join('.'),
              name: `[${arrayIndex}]: ${String(arrayValue)}`
            });
          }
        });
        return mappedTreeItems;
      }

      if (typeof inputObject === 'object') {
        Object.keys(inputObject).forEach((objectKey) => {
          const objectValue = inputObject[objectKey];
          const childPropertyPath = [...propertyPath, objectKey];

          if (isEmptyValue(objectValue)) return;

          if (typeof objectValue === 'object' && objectValue !== null) {
            const childTreeItems = mapObjectToTreeViewItems(objectValue, childPropertyPath);
            if (childTreeItems.length) {
              mappedTreeItems.push({
                id: childPropertyPath.join('.'),
                name: objectKey,
                children: childTreeItems
              });
            }
          } else {
            mappedTreeItems.push({
              id: childPropertyPath.join('.'),
              name: `${objectKey}: ${String(objectValue)}`
            });
          }
        });
        return mappedTreeItems;
      }

      mappedTreeItems.push({
        id: propertyPath.join('.'),
        name: `${propertyPath[propertyPath.length - 1] || 'value'}: ${String(inputObject)}`
      });
      return mappedTreeItems;
    };

    watch(
      () => props.isOpen,
      (isDialogOpen) => {
        isGenerateDetailsModalOpen.value = isDialogOpen;
        if (isDialogOpen && props.data) {
          treeViewItems.value = mapObjectToTreeViewItems(props.data, []);
          treeViewRefreshKey.value++;
        } else {
          treeViewItems.value = [];
        }
      },
      { immediate: true }
    );

    const handleCloseDialog = () => emit('closeDialog');

    return {
      isGenerateDetailsModalOpen,
      treeViewItems,
      treeViewRefreshKey,
      isTreeExpanded,
      handleCloseDialog
    };
  }
};
</script>

<style lang="scss" scoped>
.prevent-details-modal {
  ::v-deep .v-treeview > .v-treeview-node .v-treeview-node__label {
    white-space: normal;
    word-break: break-word;
  }
}
</style>
